import { http } from "@/lib/http";

const SESSION_STORAGE_PREFIX = "fanvault:tenant-page-analytics-session:";
const LAST_SENT_PREFIX = "fanvault:tenant-page-analytics-last:";

function buildSessionStorageKey(tenantSlug: string) {
  return `${SESSION_STORAGE_PREFIX}${tenantSlug}`;
}

function buildLastSentKey(tenantSlug: string, path: string) {
  return `${LAST_SENT_PREFIX}${tenantSlug}:${path}`;
}

function randomId() {
  return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function getSessionId(tenantSlug: string) {
  const storageKey = buildSessionStorageKey(tenantSlug);
  const existing = window.localStorage.getItem(storageKey);

  if (existing) {
    return existing;
  }

  const created = randomId();
  window.localStorage.setItem(storageKey, created);
  return created;
}

export interface TenantPageViewTrackingOptions {
  contentType?: "post" | "course" | "page";
  contentId?: number | null;
  durationSeconds?: number;
}

export function resolveContentTrackingFromPath(path: string): Pick<TenantPageViewTrackingOptions, "contentType" | "contentId"> {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const postMatch = normalizedPath.match(/^\/posts\/(\d+)(?:\/)?$/);
  const courseMatch = normalizedPath.match(/^\/posts\/folder\/(\d+)(?:\/)?$/);

  if (postMatch?.[1]) {
    return {
      contentType: "post",
      contentId: Number(postMatch[1]),
    };
  }

  if (courseMatch?.[1]) {
    return {
      contentType: "course",
      contentId: Number(courseMatch[1]),
    };
  }

  return {
    contentType: "page",
    contentId: null,
  };
}

export async function trackTenantPageView(tenantSlug: string, path: string, pageName?: string, options: TenantPageViewTrackingOptions = {}) {
  if (!tenantSlug || !path) {
    return;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const lastSentKey = buildLastSentKey(tenantSlug, normalizedPath);
  const now = Date.now();
  const lastSentAt = Number(window.sessionStorage.getItem(lastSentKey) ?? 0);

  if (!options.durationSeconds && lastSentAt && now - lastSentAt < 5000) {
    return;
  }

  if (!options.durationSeconds) {
    window.sessionStorage.setItem(lastSentKey, String(now));
  }

  try {
    await http.post("/api/v1/analytics/page-views", {
      path: normalizedPath,
      page_name: pageName || undefined,
      content_type: options.contentType,
      content_id: options.contentId || undefined,
      duration_seconds: options.durationSeconds,
      session_id: getSessionId(tenantSlug),
    });
  } catch {
    // Tracking failures should never block navigation.
  }
}
